import { useThree } from "@react-three/fiber";
import { RefObject, useEffect, useRef } from "react";
import { Group, PerspectiveCamera, Scene, Box3, Vector3, DirectionalLight } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { calculateHeightByDistanceAndAngle, getFOVFromProjectionMatrix, getScreenRelativeCameraPosition } from "./utils";
import { useDataStore, useSceneStore } from "@/zustand";
import { IRoot, ISceneComp, ITrackingTypes } from "@r3f-component-data-structure";
import { getTargetLostBehaviour } from "@/utils";

export const useGrabAndGoAnimation = (
    trackerContentGroupRef:  RefObject<Group | null>, 
    trackerGroupRef:  RefObject<Group | null>, 
    shadowObjectsGroupRef: RefObject<Group | null>,
    trackingImageRef: RefObject<Group | null>,
    isFlatOrientation: boolean,
) => {
    const controlsRef = useRef<OrbitControls>(undefined);
    const {scene, gl: renderer, set: setState} = useThree();
    const perspectiveCamRef = useRef<PerspectiveCamera>(undefined);
    const arDirectionalLightRef = useRef<DirectionalLight>(undefined);
    const componentsById = useDataStore(({componentsById}) => componentsById);
    const enterBehaviourHasRunRef = useRef(false);
    const contentScaleRef = useRef<Vector3>(null);
    const contentCenterRef = useRef<Vector3>(null);

    const enterGrabAndGoBehaviour = (onFinishCb?: () => void) => {
        // if (enterBehaviourHasRunRef.current) return;
        // enterBehaviourHasRunRef.current = true;

        const trackerContent = trackerContentGroupRef.current;
        const shadowObjectsGroup = shadowObjectsGroupRef.current;

        if (!trackerContent) return;
    
        const zapparCamera = scene.getObjectByName('zappar-camera') as PerspectiveCamera | undefined;
        const arScene = scene.getObjectByName('ArScene') as Scene | undefined;
        arDirectionalLightRef.current = scene.getObjectByName('ar-directional-light') as DirectionalLight;
        arDirectionalLightRef.current?.removeFromParent();

        if (!zapparCamera || !arScene) return;

        shadowObjectsGroup?.removeFromParent();

        const fov = getFOVFromProjectionMatrix(zapparCamera.projectionMatrix);

        const aspect = renderer.domElement.width/renderer.domElement.height;
    
        if (!perspectiveCamRef.current) {
            perspectiveCamRef.current = new PerspectiveCamera(fov, aspect, 0.01, 100);
            perspectiveCamRef.current.name = 'orbit-camera';
            const directionalLight = new DirectionalLight();

            directionalLight.position.set(0, 0, 0);
            directionalLight.target.position.set(0, 0, -1)
            directionalLight.intensity = 0.4;
            directionalLight.color.set(0xffffff);
            directionalLight.castShadow = true;

            perspectiveCamRef.current.add(directionalLight);
        }

        trackerContent?.removeFromParent();
        trackingImageRef.current?.removeFromParent()
        if (trackingImageRef.current) trackerContent?.add(trackingImageRef.current)

        if (isFlatOrientation) {
            trackerContent.rotation.set(0, 0, 0);
            trackerContent?.rotateX(-Math.PI / 2)
        };

        trackerContent?.position.copy(new Vector3(0, 0, 0));

        const bbox = new Box3().setFromObject(trackerContent);
        console.log('set content and scale ref')
        contentScaleRef.current = bbox.getSize(new Vector3())
        contentCenterRef.current = bbox.getCenter(new Vector3());

        const camPosition = getScreenRelativeCameraPosition(zapparCamera as PerspectiveCamera, contentScaleRef.current, contentCenterRef.current, aspect);
        if (isFlatOrientation) camPosition.y += calculateHeightByDistanceAndAngle(camPosition.z, 25);
        
        perspectiveCamRef.current!.position.copy(camPosition)

        arScene.add(perspectiveCamRef.current)
        
        setState({camera: perspectiveCamRef.current!})
    
        controlsRef.current = new OrbitControls(perspectiveCamRef.current!, renderer.domElement );    
        controlsRef.current.enablePan = false;
        controlsRef.current.target.copy(contentCenterRef.current);
        controlsRef.current.maxDistance = perspectiveCamRef.current.position.z * 2;
        controlsRef.current.minDistance = perspectiveCamRef.current.position.z / 4;
        if (!isFlatOrientation) {
            controlsRef.current.maxAzimuthAngle = Math.PI / 2;
            controlsRef.current.minAzimuthAngle = -Math.PI / 2;
            controlsRef.current.minPolarAngle = 0.1 ;
            controlsRef.current.maxPolarAngle = 3;
        } else {
            controlsRef.current.maxPolarAngle = Math.PI / 2;
            controlsRef.current.minPolarAngle = 0.1 ;
        }
        controlsRef.current.update();
        arScene.add(trackerContent);
        onFinishCb?.();
    }

    const exitGrabAndGoBehaviour = () => {
        if (!controlsRef.current) return;
        enterBehaviourHasRunRef.current = false;

        controlsRef.current?.dispose();
        perspectiveCamRef.current?.removeFromParent();
        trackingImageRef.current?.removeFromParent();

        const arScene = scene.getObjectByName('ArScene') as Scene | undefined;

        if (arDirectionalLightRef.current) arScene?.add(arDirectionalLightRef.current);

        const trackerContent = trackerContentGroupRef.current;
        trackerContent?.rotation.set(0, 0, 0);
        const trackerContentParent = trackerGroupRef.current;
        const shadowObjectsGroup = shadowObjectsGroupRef.current;

        if (!trackerContent) return;

        const activeScene = componentsById?.[useSceneStore.getState().activeSceneId!] as ISceneComp | undefined
        if (!activeScene) return;

        const defaultCamera = getDefaultCameraByScene(activeScene, scene)     
        if (!defaultCamera) return;
    
        setState({camera: defaultCamera})

        trackerContent?.removeFromParent();
        trackerContent?.position.set(0, 0, 0);
    
        if (shadowObjectsGroup) trackerContent?.add(shadowObjectsGroup);
        trackerContentParent?.add(trackerContent);
    }

    return {enterGrabAndGoBehaviour, exitGrabAndGoBehaviour, controlsRef}
}

const getDefaultCameraByScene = (scene: ISceneComp, threeScene: Scene) => {
    switch (scene.trackingType) {
        case ITrackingTypes.content360:
            return threeScene.getObjectByName('drag-camera') as PerspectiveCamera | undefined;
        case ITrackingTypes.face:
        case ITrackingTypes.world:
        case ITrackingTypes.image:
            return threeScene.getObjectByName('zappar-camera') as PerspectiveCamera | undefined;
        default:
            null      
    }  
}

export const useHasTrackingImageChanged = () => {
    const activeSceneId = useSceneStore((state) => state.activeSceneId);
    const prevSceneId = useSceneStore((state) => state.previousSceneId);
    const componentsById = useDataStore(({componentsById}) => componentsById);
    return hasTrackingImageChanged(componentsById?.[prevSceneId!] as ISceneComp | undefined, componentsById?.[activeSceneId!] as ISceneComp);
}

const hasTrackingImageChanged = (prevScene: ISceneComp | undefined, newScene: ISceneComp): null | boolean => {
    if (prevScene?.trackingType !== ITrackingTypes.image || newScene.trackingType !== ITrackingTypes.image) return null;
    return prevScene.trackingImageId !== newScene.trackingImageId;
}

export const useProjectHasImageTrackedScenes = (): boolean => {
    const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
    const root = componentsById?.[rootId || ''] as IRoot | undefined;
    const numImageTrackedScenes = root?.children?.filter(id => (componentsById?.[id] as ISceneComp).trackingType === ITrackingTypes.image)?.length;
    return (numImageTrackedScenes ?? 0) > 0;
}

export const useProjectHasWorldTrackedScenes = (): boolean => {
    const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
    const root = componentsById?.[rootId || ''] as IRoot | undefined;
    const numWorldTrackedScenes = root?.children?.filter(id => (componentsById?.[id] as ISceneComp).trackingType === ITrackingTypes.world)?.length;
    return (numWorldTrackedScenes ?? 0) > 0;
}

export const useProjectHasFaceTrackedScenes = (): boolean => {
    const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
    const root = componentsById?.[rootId || ''] as IRoot | undefined;
    const numFaceTrackedScenes = root?.children?.filter(id => (componentsById?.[id] as ISceneComp).trackingType === ITrackingTypes.face)?.length;
    return (numFaceTrackedScenes ?? 0) > 0;
}

export const useGetTargetLostBehaviour = () => {
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const rootId = useDataStore(({ rootId }) => rootId);
    return getTargetLostBehaviour(componentsById, rootId)
}

export const useSubscribeToState = <T>(state: T): RefObject<T> => {
    const stateRef = useRef(state);
	useEffect(() => {
		stateRef.current = state;
	}, [state]);
    return stateRef;
}
 